// src/components/UserLogin/UserLogin.jsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { FormControl } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Transition } from "react-transition-group";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import styles from "./UserLogin.module.css";
import { useAuth } from "../../contexts/AuthContext";
import { useUtil } from "../../contexts/UtilContext";
import {
  eighteenYearsAgo,
  isValidEmail,
  isValidPhone,
  sendEmailVerification,
  validatePassword,
  verifyAndFetchUserRoles,
} from "../../util/utilFunctions";
import TermsModal from "./TermsModal";

const DIALOG_FADE_MS = 300;

function UserLogin() {
  const { loginUser, openLogin, closeLoginModal } = useAuth();
  const { showLoader, hideLoader } = useUtil();

  // view state
  const [visible, setVisible] = useState(false); // false = Login, true = Register
  const [resetVisible, setResetVisible] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const [resetLinkSuccess, setResetLinkSuccess] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  // form + flow state
  const [step, setStep] = useState(1);
  const [timer, setTimer] = useState(0);
  const [showResendButton, setShowResendButton] = useState(false);
  const [modalError, setModalError] = useState("");

  const [passResetDetails, setPassResetDetails] = useState({ email: "" });
  const [loginDetails, setLoginDetails] = useState({ email: "", password: "" });
  const [emailDetails, setEmailDetails] = useState({ email: "" });
  const [basicDetails, setBasicDetails] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
    password: "",
    confirmPassword: "",
  });
  const [verificationDetails, setVerificationDetails] = useState({
    verificationCode: "",
  });

  // ---------- FULL RESET ----------
  const resetAll = useCallback(() => {
    setVisible(false);
    setResetVisible(false);
    setExistingUser(false);
    setResetLinkSuccess(false);
    setTermsModalOpen(false);

    setStep(1);
    setTimer(0);
    setShowResendButton(false);
    setModalError("");

    setPassResetDetails({ email: "" });
    setLoginDetails({ email: "", password: "" });
    setEmailDetails({ email: "" });
    setBasicDetails({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      dateOfBirth: "",
      password: "",
      confirmPassword: "",
    });
    setVerificationDetails({ verificationCode: "" });
  }, []);

  // Clear forms when the modal closes
  useEffect(() => {
    if (!openLogin) resetAll();
  }, [openLogin, resetAll]);

  // ---- Route-change reset WITHOUT useLocation (works even outside <Router>) ----
  useEffect(() => {
    if (typeof window === "undefined") return;

    const fireLocationChange = () =>
      window.dispatchEvent(new Event("locationchange"));

    const onLocationChange = () => {
      // Any navigation should wipe inputs
      resetAll();
    };

    const origPushState = history.pushState;
    const origReplaceState = history.replaceState;

    history.pushState = function (...args) {
      const ret = origPushState.apply(this, args);
      fireLocationChange();
      return ret;
    };
    history.replaceState = function (...args) {
      const ret = origReplaceState.apply(this, args);
      fireLocationChange();
      return ret;
    };

    window.addEventListener("popstate", fireLocationChange);
    window.addEventListener("locationchange", onLocationChange);

    return () => {
      history.pushState = origPushState;
      history.replaceState = origReplaceState;
      window.removeEventListener("popstate", fireLocationChange);
      window.removeEventListener("locationchange", onLocationChange);
    };
  }, [resetAll]);

  // timer for resend code
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (timer === 0) {
      setShowResendButton(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // ---------- validators ----------
  const validateEmailDetails = () => {
    setModalError("");
    if (!emailDetails.email)
      return setModalError("Please provide your Email"), false;
    if (!isValidEmail(emailDetails.email))
      return setModalError("Please provide a valid Email format"), false;
    return true;
  };

  const validateBasicDetails = () => {
    setModalError("");

    if (!basicDetails.firstName)
      return setModalError("Please provide your First Name"), false;
    if (!basicDetails.lastName)
      return setModalError("Please provide your Last Name"), false;

    if (basicDetails.phoneNumber && !isValidPhone(basicDetails.phoneNumber))
      return setModalError("Please provide a valid Phone Number"), false;

    if (!basicDetails.dateOfBirth)
      return setModalError("Please provide your Date of Birth"), false;

    const dob = dayjs(basicDetails.dateOfBirth);
    const age = dayjs().diff(dob, "year");
    if (age < 18)
      return setModalError("You must be at least 18 years old"), false;

    if (!basicDetails.password)
      return setModalError("Please provide a Password"), false;
    if (!validatePassword(basicDetails.password))
      return (
        setModalError(
          "Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character."
        ),
        false
      );

    if (!basicDetails.confirmPassword)
      return setModalError("Please confirm the Password"), false;
    if (basicDetails.password !== basicDetails.confirmPassword)
      return setModalError("Passwords should match"), false;

    return true;
  };

  const validateVerificationDetails = () => {
    setModalError("");
    if (!verificationDetails.verificationCode)
      return setModalError("Please provide the verification code."), false;
    return true;
  };

  const startTimer = () => {
    setTimer(60);
    setShowResendButton(false);
  };

  // ---------- handlers ----------
  const handlePassResetChange = (e) => {
    const { name, value } = e.target;
    setPassResetDetails((s) => ({ ...s, [name]: value }));
  };
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginDetails((s) => ({ ...s, [name]: value }));
  };
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailDetails((s) => ({ ...s, [name]: value }));
  };
  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicDetails((s) => ({ ...s, [name]: value }));
  };
  const handleVerificationChange = (e) => {
    const { name, value } = e.target;
    setVerificationDetails((s) => ({ ...s, [name]: value }));
  };
  const handleDOBChange = (value) => {
    setBasicDetails((prev) => ({ ...prev, dateOfBirth: value }));
  };

  const handleResendCode = async () => {
    try {
      const response = await sendEmailVerification(
        emailDetails.email,
        false,
        basicDetails.firstName,
        basicDetails.lastName
      );
      if (response.status === 200 && response.data?.success) {
        startTimer();
      } else {
        setModalError("Failed to resend verification code. Please try again.");
      }
    } catch {
      setModalError("An error occurred while resending the verification code.");
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();

    if (step === 1) {
      if (!validateEmailDetails()) return;
      const response = await verifyAndFetchUserRoles(emailDetails.email);
      if (response.status === 200 && response.data?.success) {
        if (response.data.existingUser) setExistingUser(true);
        else setStep(2);
      } else {
        setModalError(
          "Some error occurred while sending verification email. Please try again!"
        );
      }
    } else if (step === 2) {
      if (!validateBasicDetails()) return;
      const response = await sendEmailVerification(
        emailDetails.email,
        false,
        basicDetails.firstName,
        basicDetails.lastName
      );
      if (response.status === 200 && response.data?.success) {
        setStep(3);
        startTimer();
      } else {
        setModalError(
          "Some error occurred while sending verification email. Please try again!"
        );
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateVerificationDetails()) return;

    try {
      showLoader();
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/auth/signup`,
        {
          firstName: basicDetails.firstName,
          lastName: basicDetails.lastName,
          email: emailDetails.email,
          password: basicDetails.password,
          phoneNumber: basicDetails.phoneNumber,
          dateOfBirth: basicDetails.dateOfBirth
            ? basicDetails.dateOfBirth.format("YYYY-MM-DD")
            : null,
          verificationCode: verificationDetails.verificationCode,
        }
      );

      if (response.status === 200 && response.data?.success) {
        loginUser(
          response.data.accessToken || "",
          response.data.userDetails || {}
        );
        closeLoginModal();
        window.location.href = `/MyFirstLand?newUser=true`;
      } else {
        setModalError(
          response.data?.message ||
            "Some error occurred while creating the user!"
        );
      }
    } catch {
      setModalError("Error during sign-up!");
    } finally {
      hideLoader();
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setModalError("");

    if (!loginDetails.email) return setModalError("Please provide your Email");
    if (!isValidEmail(loginDetails.email))
      return setModalError("Please provide a valid Email format");
    if (!loginDetails.password)
      return setModalError("Please provide a Password");

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/auth/login`,
        { email: loginDetails.email, password: loginDetails.password }
      );

      if (response.status === 200 && response.data?.success) {
        loginUser(
          response.data.accessToken || "",
          response.data.userDetails || {}
        );
        resetAll();
        closeLoginModal();
        toast.success("Logged in successfully.");
      } else {
        setModalError(
          response.data?.message || "Some error occurred while trying to login!"
        );
      }
    } catch {
      setModalError("Error during sign-in!");
    }
  };

  // Switch Login <-> Register and nuke all inputs
  const handleSwitch = () => {
    setModalError("");
    const goingToRegister = !visible;
    resetAll();
    setVisible(goingToRegister); // show the target view after reset
  };

  // Toggle Reset Password and clear that form
  const handlePassReset = useCallback(() => {
    setModalError("");
    setResetVisible((v) => !v);
    setPassResetDetails({ email: "" });
    setResetLinkSuccess(false);
  }, []);

  const handleEUProceed = () => {
    setExistingUser(false);
    setLoginDetails((s) => ({ ...s, email: emailDetails.email }));
    const emailToKeep = emailDetails.email;
    resetAll();
    setLoginDetails({ email: emailToKeep, password: "" });
    setVisible(false);
  };

  const handleEUNewEmail = () => {
    setEmailDetails({ email: "" });
    setExistingUser(false);
  };

  // ---------- Transition fix: put Transition INSIDE the modal ----------
  const dialogRef = useRef(null);

  return (
    <>
      <Modal
        keepMounted
        open={openLogin}
        onClose={() => {
          closeLoginModal();
          // resetAll(); // handled by effect when openLogin -> false
        }}
        slotProps={{
          backdrop: {
            sx: {
              transition: "opacity 400ms, backdrop-filter 400ms",
              opacity: openLogin ? 1 : 0,
              backdropFilter: openLogin ? "blur(8px)" : "none",
            },
          },
        }}
      >
        <ModalDialog
          aria-labelledby="fade-modal-dialog-title"
          aria-describedby="fade-modal-dialog-description"
          sx={{
            maxHeight: "85vh",
            overflow: "auto",
            p: 0,
            boxShadow: "none",
            background: "transparent",
          }}
        >
          <Transition
            in={openLogin}
            timeout={DIALOG_FADE_MS}
            nodeRef={dialogRef}
            mountOnEnter
            unmountOnExit
          >
            {(state) => (
              <div
                ref={dialogRef}
                style={{
                  opacity: state === "entered" ? 1 : 0,
                  transform: state === "entered" ? "scale(1)" : "scale(0.98)",
                  transition: `opacity ${DIALOG_FADE_MS}ms ease, transform ${DIALOG_FADE_MS}ms ease`,
                  background: "var(--joy-palette-background-body, #fff)",
                  borderRadius: 12,
                  boxShadow:
                    "0 10px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <div className={styles.modalContainer}>
                  {/* LOGIN / RESET */}
                  <div className={visible ? styles.hidden : styles.visible}>
                    {!resetVisible ? (
                      <>
                        <h2 className={styles.loginTitle}>Log In</h2>
                        <form
                          className={styles.formContainer}
                          autoComplete="off"
                        >
                          <div className={styles.formComponent}>
                            <TextField
                              id="LoginEmail"
                              label="Email"
                              name="email"
                              variant="outlined"
                              required
                              type="email"
                              fullWidth
                              focused
                              value={loginDetails.email}
                              onChange={handleLoginChange}
                              autoComplete="off"
                            />
                          </div>
                          <div className={styles.formComponent}>
                            <TextField
                              id="LoginPassword"
                              label="Password"
                              name="password"
                              variant="outlined"
                              required
                              type="password"
                              fullWidth
                              value={loginDetails.password}
                              onChange={handleLoginChange}
                              autoComplete="off"
                            />
                          </div>
                          <div
                            className={`${styles.formComponent} ${styles.padT15}`}
                          >
                            <Button
                              variant="contained"
                              onClick={handleLogin}
                              size="large"
                            >
                              Login
                            </Button>
                          </div>
                          <div className={styles.formComponent}>
                            {modalError && (
                              <p style={{ color: "red" }}>{modalError}</p>
                            )}
                          </div>
                        </form>

                        <div className={styles.loginChangeComponent}>
                          Forgot password?{" "}
                          <Button
                            variant="contained"
                            onClick={handlePassReset}
                            size="small"
                          >
                            Reset
                          </Button>
                        </div>
                        <div className={styles.loginChangeComponent}>
                          Don&apos;t have an account?{" "}
                          <Button
                            variant="contained"
                            onClick={() => setTermsModalOpen(true)}
                            size="small"
                          >
                            Register
                          </Button>
                          <TermsModal
                            open={termsModalOpen}
                            onClose={() => setTermsModalOpen(false)}
                            onAccept={() => {
                              setTermsModalOpen(false);
                              handleSwitch(); // show Register after clearing state
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <h2 className={styles.loginTitle}>Reset Password</h2>
                        {resetLinkSuccess ? (
                          <div className={styles.resetSuccess}>
                            Password reset link has been sent to your email!
                          </div>
                        ) : (
                          <form
                            className={styles.formContainer}
                            autoComplete="off"
                          >
                            <div>
                              Enter your email to receive the reset password
                              link!
                            </div>
                            <div className={styles.formComponent}>
                              <TextField
                                id="ResetPasswordEmail"
                                label="Email"
                                name="email"
                                variant="outlined"
                                required
                                type="email"
                                fullWidth
                                value={passResetDetails.email}
                                onChange={handlePassResetChange}
                                autoComplete="off"
                              />
                            </div>
                            <div
                              className={`${styles.formComponent} ${styles.padT15}`}
                            >
                              <Button
                                variant="contained"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  if (!passResetDetails.email)
                                    return setModalError(
                                      "Please provide your Email"
                                    );
                                  if (!isValidEmail(passResetDetails.email))
                                    return setModalError(
                                      "Please provide a valid Email format"
                                    );
                                  try {
                                    const response = await axios.post(
                                      `${
                                        import.meta.env.VITE_BACKEND_URL
                                      }/auth/reset-pass/send-link`,
                                      { email: passResetDetails.email }
                                    );
                                    if (
                                      response.status === 200 &&
                                      response.data?.success
                                    ) {
                                      setResetLinkSuccess(true);
                                    } else {
                                      setModalError(
                                        response.data?.message ||
                                          "Some error occurred while sending the password reset link!"
                                      );
                                    }
                                  } catch {
                                    setModalError(
                                      "Error sending password reset link!"
                                    );
                                  }
                                }}
                                size="large"
                              >
                                Send Link
                              </Button>
                            </div>
                            <div className={styles.formComponent}>
                              {modalError && (
                                <p style={{ color: "red" }}>{modalError}</p>
                              )}
                            </div>
                          </form>
                        )}
                        <div className={styles.loginChangeComponent}>
                          Go back to login?{" "}
                          <Button
                            variant="contained"
                            onClick={handlePassReset}
                            size="small"
                          >
                            Login
                          </Button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* REGISTER */}
                  <div className={!visible ? styles.hidden : styles.visible}>
                    <h2 className={styles.loginTitle}>Register</h2>
                    {existingUser ? (
                      <div className={styles.existingUserContainer}>
                        <div className={styles.existingUserTitle}>
                          A user account already exists with the email:{" "}
                          <b>{emailDetails.email}</b>
                        </div>
                        <div className={styles.existingUserQuestion}>
                          Do you wish to sign in using this email?
                        </div>
                        <div className={styles.existingUserButtons}>
                          <Button
                            variant="outlined"
                            onClick={handleEUNewEmail}
                            size="large"
                          >
                            Use a different email
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleEUProceed}
                            size="large"
                          >
                            Yes
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.formContainer}>
                        {step === 1 && (
                          <form
                            className={styles.formSection}
                            autoComplete="off"
                          >
                            <div className={styles.formComponent}>
                              <TextField
                                id="SignupEmail"
                                name="email"
                                label="Email"
                                variant="outlined"
                                required
                                type="email"
                                fullWidth
                                value={emailDetails.email}
                                onChange={handleEmailChange}
                                autoComplete="off"
                              />
                            </div>
                            <div
                              className={`${styles.formComponent} ${styles.padT15}`}
                            >
                              <Button
                                variant="contained"
                                onClick={handleNext}
                                size="large"
                              >
                                Next
                              </Button>
                            </div>
                          </form>
                        )}

                        {step === 2 && (
                          <form
                            className={styles.formSection}
                            autoComplete="off"
                          >
                            <div className={styles.formComponent}>
                              <TextField
                                id="SignupFirstName"
                                label="First Name"
                                name="firstName"
                                variant="outlined"
                                required
                                type="text"
                                fullWidth
                                value={basicDetails.firstName}
                                onChange={handleBasicChange}
                                autoComplete="off"
                              />
                            </div>
                            <div className={styles.formComponent}>
                              <TextField
                                id="SignupLastName"
                                label="Last Name"
                                name="lastName"
                                variant="outlined"
                                required
                                type="text"
                                fullWidth
                                value={basicDetails.lastName}
                                onChange={handleBasicChange}
                                autoComplete="off"
                              />
                            </div>
                            <div className={styles.formComponent}>
                              <TextField
                                id="SignUpPhoneNumber"
                                label="Phone Number"
                                name="phoneNumber"
                                variant="outlined"
                                required
                                type="tel"
                                inputProps={{
                                  maxLength: 10,
                                  inputMode: "numeric",
                                }}
                                fullWidth
                                value={basicDetails.phoneNumber}
                                onChange={handleBasicChange}
                                autoComplete="off"
                              />
                            </div>
                            <FormControl fullWidth className={styles.marT10Imp}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  id="dateOfBirth"
                                  name="dateOfBirth"
                                  label="DOB *"
                                  maxDate={eighteenYearsAgo}
                                  value={
                                    basicDetails.dateOfBirth
                                      ? dayjs(basicDetails.dateOfBirth)
                                      : null
                                  }
                                  onChange={handleDOBChange}
                                />
                              </LocalizationProvider>
                            </FormControl>
                            <div className={styles.formComponent}>
                              <TextField
                                id="SignupPassword"
                                label="Password"
                                name="password"
                                variant="outlined"
                                required
                                type="password"
                                fullWidth
                                value={basicDetails.password}
                                onChange={handleBasicChange}
                                autoComplete="off"
                              />
                            </div>
                            <div className={styles.formComponent}>
                              <TextField
                                id="SignupConfirmPassword"
                                label="Confirm Password"
                                name="confirmPassword"
                                variant="outlined"
                                required
                                type="password"
                                fullWidth
                                value={basicDetails.confirmPassword}
                                onChange={handleBasicChange}
                                autoComplete="off"
                              />
                            </div>
                            <div
                              className={`${styles.formComponent} ${styles.padT15}`}
                            >
                              <Button
                                variant="contained"
                                onClick={handleNext}
                                size="large"
                              >
                                Next
                              </Button>
                            </div>
                          </form>
                        )}

                        {step === 3 && (
                          <form
                            className={styles.formSection}
                            autoComplete="off"
                          >
                            <div>Email Verification</div>
                            <div
                              className={`${styles.formComponent} ${styles.componentType1}`}
                            >
                              <TextField
                                id="verificationCode"
                                name="verificationCode"
                                label="Verification Code"
                                variant="outlined"
                                type="text"
                                fullWidth
                                required
                                value={verificationDetails.verificationCode}
                                onChange={handleVerificationChange}
                                autoComplete="off"
                              />
                              <div style={{ marginTop: 10 }}>
                                {showResendButton ? (
                                  <Button onClick={handleResendCode}>
                                    Resend Code
                                  </Button>
                                ) : (
                                  <div>
                                    You can resend code after {timer} seconds
                                  </div>
                                )}
                              </div>
                            </div>
                            <div
                              className={`${styles.formComponent} ${styles.padT15}`}
                            >
                              <Button
                                variant="contained"
                                onClick={handleSubmit}
                                size="large"
                              >
                                Submit
                              </Button>
                            </div>
                            {modalError && (
                              <div className={styles.formComponent}>
                                <p style={{ color: "red" }}>{modalError}</p>
                              </div>
                            )}
                          </form>
                        )}

                        <div className={styles.loginChangeComponent}>
                          Already have an account?{" "}
                          <Button
                            variant="contained"
                            onClick={handleSwitch}
                            size="small"
                          >
                            Log In
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Transition>
        </ModalDialog>
      </Modal>
    </>
  );
}

export default UserLogin;
